import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import queryString from "query-string";
import withStyles from "@material-ui/core/styles/withStyles";
import Table from "@material-ui/core/Table";
import Typography from "@material-ui/core/Typography";
import OSSTableHead from "./OSSTableHead";
import OSSTableBody from "./OSSTableBody";
import OSSTablePagination from "./OSSTablePagination/OSSTablePagination";

const styles = theme => ({
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    background: "#fcfcfc"
  },
  noResultsText: {
    fontStyle: "italic"
  }
});

const OSSTable = ({
  classes = {},
  config = {},
  data = {},
  history,
  location,
  showPagination = false,
  componentContext = "",
  updateExternalQuery = false,
  externalQueryUpdater = () => {},
  TableProps,
  NoTableDataMessage = () => (
    <Typography className={classes.noResultsText}>
      Sorry, no results found.
    </Typography>
  )
}) => {
  const { query } = data;
  const [tableQuery, setTableQuery] = useState(query);
  const {
    columns,
    TableHeadProps,
    activeColumnHandler,
    sortHandler,
    sortDirectionHandler,
    changePageHandler,
    changeRowsPerPageHandler
  } = config;
  const { ordering } = tableQuery;

  useEffect(() => {
    setTableQuery(query);
  }, [query]);

  /**
   * We can have the table either maintian internal state
   * of the query, and push updates to the url, and expect the
   * parent component to listen for changes to the url
   * OR
   * we can have the table push updates directly to the parent
   * component's query, and have the parent manage the url updates
   *
   * @param {Object} newQuery
   */
  const updateQueryAndPushToHistory = newQuery => {
    if (updateExternalQuery) {
      externalQueryUpdater(newQuery);
    } else {
      setTableQuery(newQuery);
      history.push({
        path: location.pathname,
        search: queryString.stringify(newQuery)
      });
    }
  };

  const onSortByProperty = property => {
    let newQuery;

    if (ordering && ordering.substr(1) === property) {
      newQuery =
        ordering === property
          ? Object.assign({}, tableQuery, { ordering: `-${property}` })
          : Object.assign({}, tableQuery, { ordering: property });
    } else {
      newQuery = Object.assign({}, tableQuery, { ordering: `-${property}` });
    }

    updateQueryAndPushToHistory(newQuery);
  };

  const onChangePage = page => {
    let offset = page * query.limit;
    const newQuery = Object.assign({}, query, { offset });
    window.scrollTo(0, 0);
    updateQueryAndPushToHistory(newQuery);
  };

  const onChangeRowsPerPage = limit => {
    const newQuery = Object.assign({}, query, { limit });
    updateQueryAndPushToHistory(newQuery);
  };

  return (
    <div className={classes.tableWrapper}>
      <Table size="small" {...TableProps}>
        {/* TABLE HEAD  */}
        <OSSTableHead
          columns={columns}
          query={query}
          sortDirectionHandler={sortDirectionHandler}
          activeColumnHandler={activeColumnHandler}
          onRequestSort={sortHandler ? sortHandler : onSortByProperty}
          TableHeadProps={TableHeadProps}
          componentContext={componentContext}
        />

        {/* TABLE BODY */}
        <OSSTableBody
          config={config}
          data={data}
          NoTableDataMessage={NoTableDataMessage}
        />
      </Table>

      {/* OPTIONAL TABLE PAGINATION */}
      {showPagination && (
        <OSSTablePagination
          data={data}
          query={query}
          onChangePage={changePageHandler || onChangePage}
          onChangeRowsPerPage={changeRowsPerPageHandler || onChangeRowsPerPage}
        />
      )}
    </div>
  );
};

OSSTable.propTypes = {
  classes: PropTypes.object.isRequired,
  config: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    sortHandler: PropTypes.func,
    sortDirectionHandler: PropTypes.func,
    activeColumnHandler: PropTypes.func
  }).isRequired,
  data: PropTypes.shape({
    count: PropTypes.number.isRequired,
    query: PropTypes.object.isRequired,
    results: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  showPagination: PropTypes.bool
};

export default withRouter(withStyles(styles)(OSSTable));
